import {
  ClientClock,
  type ClientMsg,
  decodeServer,
  encodeClient,
  INPUT_REDUNDANCY,
  PendingInputs,
  PROTOCOL_VERSION,
  type ServerMsg,
  type Snapshot,
  Tag,
  type TickedInput,
  type Welcome,
} from '@battle/netcode';

/**
 * Client side of the match protocol (docs/05): one WebSocket to /play/<gs>?room=<code>, the
 * Join/Welcome handshake, redundant input packets, pings for RTT, and a network-condition
 * simulator (`?net=latency,jitter,loss` in ms/ms/%) applied in both directions.
 */
export interface NetConditions {
  latencyMs: number;
  jitterMs: number;
  lossPct: number;
}

export function parseNetConditions(value: string | null): NetConditions | null {
  if (!value) return null;
  const [l = 0, j = 0, p = 0] = value.split(',').map(Number);
  return { latencyMs: l / 2, jitterMs: j / 2, lossPct: p };
}

export interface NetStats {
  rttMs: number;
  jitterMs: number;
  sent: number;
  received: number;
  lost: number;
  bytesIn: number;
  bytesOut: number;
}

export interface Connection {
  readonly welcome: Welcome | null;
  readonly clock: ClientClock;
  readonly pending: PendingInputs;
  readonly stats: NetStats;
  readonly state: 'connecting' | 'joined' | 'closed';
  readonly error: string | null;
  onSnapshot: ((s: Snapshot, arrivedAt: number) => void) | null;
  sendInput(input: TickedInput): void;
  close(): void;
}

export function connect(opts: {
  gs: string;
  room: string;
  creature: string;
  name: string;
  conditions: NetConditions | null;
  onWelcome: (w: Welcome) => void;
}): Connection {
  const url = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/play/${opts.gs}?room=${encodeURIComponent(opts.room)}`;
  const ws = new WebSocket(url);
  ws.binaryType = 'arraybuffer';
  const clock = new ClientClock();
  const pending = new PendingInputs();
  const stats: NetStats = {
    rttMs: 0,
    jitterMs: 0,
    sent: 0,
    received: 0,
    lost: 0,
    bytesIn: 0,
    bytesOut: 0,
  };
  let state: Connection['state'] = 'connecting';
  let error: string | null = null;
  let welcome: Welcome | null = null;
  let lastAck = 0;
  let pingTimer: ReturnType<typeof setInterval> | undefined;

  // Condition simulator: each direction is delayed by latency ± jitter and may be dropped. Order
  // is preserved (TCP semantics), so jitter shows up as bunching, like a real WebSocket.
  const cond = opts.conditions;
  let lastOut = 0;
  let lastIn = 0;
  const delayFor = (last: number) => {
    if (!cond) return 0;
    const d = cond.latencyMs + (Math.random() * 2 - 1) * cond.jitterMs;
    // Strictly after the previous message: equal deadlines can fire out of order.
    return Math.max(d, last - performance.now() + 1);
  };
  const dropped = () => !!cond && Math.random() * 100 < cond.lossPct;
  const rttStall = () => (cond ? cond.latencyMs * 2 + 20 : 0);

  const rawSend = (bytes: Uint8Array<ArrayBuffer>) => {
    if (ws.readyState !== WebSocket.OPEN) return;
    stats.sent++;
    stats.bytesOut += bytes.length;
    ws.send(bytes);
  };
  const send = (m: ClientMsg, reliable = false) => {
    const bytes = encodeClient(m);
    if (!cond) return rawSend(bytes);
    // WebSocket is reliable; "loss" models the stall a lost TCP segment causes. With a steady
    // 60 Hz stream, fast retransmit recovers in about one RTT.
    const stall = !reliable && dropped() ? rttStall() : 0;
    const d = delayFor(lastOut) + stall;
    lastOut = performance.now() + d;
    setTimeout(() => rawSend(bytes), d);
  };

  const handle = (msg: ServerMsg, arrivedAt: number) => {
    switch (msg.t) {
      case Tag.Welcome:
        welcome = msg;
        state = 'joined';
        opts.onWelcome(msg);
        break;
      case Tag.Snapshot:
        lastAck = msg.serverTick;
        api.onSnapshot?.(msg, arrivedAt);
        break;
      case Tag.Pong:
        clock.onPong(msg.clientTime, performance.now());
        stats.rttMs = clock.rttMs;
        stats.jitterMs = clock.jitterMs;
        break;
      case Tag.Error:
        error = msg.message;
        break;
    }
  };

  ws.onopen = () => {
    send(
      { t: Tag.Join, protocol: PROTOCOL_VERSION, creature: opts.creature, name: opts.name },
      true,
    );
    const ping = () => send({ t: Tag.Ping, clientTime: performance.now() }, true);
    ping();
    pingTimer = setInterval(ping, 500);
  };
  ws.onmessage = (e) => {
    const bytes = new Uint8Array(e.data as ArrayBuffer);
    stats.received++;
    stats.bytesIn += bytes.length;
    let msg: ServerMsg;
    try {
      msg = decodeServer(bytes);
    } catch {
      return;
    }
    if (!cond) return handle(msg, performance.now());
    const stall = msg.t === Tag.Snapshot && dropped() ? rttStall() : 0;
    const d = delayFor(lastIn) + stall;
    lastIn = performance.now() + d;
    setTimeout(() => handle(msg, performance.now()), d);
  };
  ws.onclose = () => {
    state = 'closed';
    clearInterval(pingTimer);
  };

  const api: Connection = {
    get welcome() {
      return welcome;
    },
    clock,
    pending,
    stats,
    get state() {
      return state;
    },
    get error() {
      return error;
    },
    onSnapshot: null,
    sendInput(input) {
      pending.add(input);
      send({ t: Tag.Inputs, ack: lastAck, inputs: pending.recent(INPUT_REDUNDANCY) });
    },
    close() {
      clearInterval(pingTimer);
      ws.close();
    },
  };
  return api;
}
